import { useMemo } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import { Table } from "@similarweb/pro-logic-components";

import { swSettings } from "app/common/services/swSettings";
import { useSubtitleFilters } from "app/custom-hooks/useSubtitleFilters";
import type { BenchmarkUrlParams } from "app/pages/benchmark-tracker/types/paramsTypes";
import DurationService from "app/services/DurationService";
import { getPaidExcelProps } from "app/services/PaidExcelService/getPaidExcelProps";
import { isPaidExcelAllowed } from "app/services/PaidExcelService/isPaidExcelAllowed";
import { useTrack } from "pro-features/components/WithTrack/src/useTrack";
import { useTranslation } from "pro-features/components/WithTranslation";

import { SUBTITLE_FILTERS } from "../constants";

interface ICommonTableTopProps {
    excelDownload: {
        url: string;
        rowCount: number;
    };
    onSearchChange: (search: string) => void;
    title: string;
}

export const CommonTableTop = ({ excelDownload, title, onSearchChange }: ICommonTableTopProps) => {
    const translate = useTranslation();
    const [track] = useTrack();
    const filters = useSubtitleFilters(...SUBTITLE_FILTERS);
    const {
        params: { key: benchmarkId, granularity, duration },
    } = useCurrentStateAndParams<BenchmarkUrlParams>();

    const handleExcelClick = () => {
        track("button", "click", "benchmark_channels_excel_export", {
            subtype: "share_of_website_traffic",
        });
    };

    const handleExcelDownloaded = () => {
        track("button", "success", "benchmark_channels_excel_export", {
            subtype: "share_of_website_traffic",
        });
    };

    const paidExcelProps = useMemo(() => {
        if (!isPaidExcelAllowed()) {
            return null;
        }

        return getPaidExcelProps();
    }, []);

    const startDate = DurationService.getDurationData(duration).forAPI.iso.from;
    const excelDownloadUrl = `${excelDownload.url}?startDate=${startDate}&granularity=${granularity}&benchmarkId=${benchmarkId}`;

    return (
        <Table.TableTop borderBottom>
            <Table.TableTop.Title
                title={title}
                filters={filters}
            />
            <Table.TableTop.Filters
                filters={[
                    {
                        filter: "search",
                        placeholder: translate("bt.report.channels.search-placeholder"),
                        onSearchChange,
                    },
                ]}
            />
            <Table.TableTop.Settings
                excelExportConfig={
                    swSettings.current.resources.IsExcelAllowed
                        ? {
                              excelDownloadUrl,
                              ...(paidExcelProps ?? {}),
                              withPaidExcel: !!paidExcelProps,
                              totalCount: excelDownload.rowCount,
                              onExcelClick: handleExcelClick,
                              onExcelDownloaded: handleExcelDownloaded,
                          }
                        : undefined
                }
            />
        </Table.TableTop>
    );
};

interface ICommonTableTopEmptyProps {
    title: string;
}

export const CommonTableTopEmpty = ({ title }: ICommonTableTopEmptyProps) => {
    const filters = useSubtitleFilters(...SUBTITLE_FILTERS);

    return (
        <Table.TableTop borderBottom>
            <Table.TableTop.Title
                title={title}
                filters={filters}
            />
        </Table.TableTop>
    );
};
